import {
  Avatar,
  AvatarGroup,
  Center,
  Popover,
  PopoverTrigger,
  Spinner,
} from '@chakra-ui/react'
import React, { lazy, Suspense, useContext, useMemo } from 'react'
import { ThreadFilter, ThreadSort } from '../../../../constants'
import type {
  FileDetailsResponse,
  MediaLibrarySearchFileResponse,
  Threads,
} from '../../../../interfaces'
import { MLContext } from '../../../../providers'
import { MediaLibraryActions } from '../../../../providers/MLProvider'

const CommentContainer = lazy(() => import('./CommentContainer'))
const CommentThread = lazy(() => import('./CommentThread'))

const LoadingState = () => (
  <Center>
    <Spinner
      thickness="4px"
      speed="0.65s"
      emptyColor="appPaleGrey"
      color="appBlue"
      size="xl"
    />
  </Center>
)

interface Props {
  fileDetails: MediaLibrarySearchFileResponse | FileDetailsResponse
}

const ThreadAvatarOverlay = ({
  fileDetails,
}: Props): React.ReactElement | null => {
  const [{ threads = [], selectedThread, threadFilter, threadSort }, dispatch] =
    useContext(MLContext)
  const [hoveredThreadId, setHoveredThreadId] = React.useState<string | null>(
    null,
  )

  const threadList = useMemo(() => {
    const filterFunction = (thread: Threads): boolean => {
      switch (threadFilter) {
        case ThreadFilter.UNRESOLVED:
          return !thread.resolved
        default:
          return true
      }
    }

    const sortCriteria = {
      [ThreadSort.DESC_UPDATED]: (a: Threads, b: Threads) =>
        a.updatedAt > b.updatedAt ? -1 : a.updatedAt < b.updatedAt ? 1 : 0,
      [ThreadSort.DESC_CREATED]: (a: Threads, b: Threads) =>
        a.createdAt > b.createdAt ? -1 : a.createdAt < b.createdAt ? 1 : 0,
    }

    return threads.filter(filterFunction).sort(sortCriteria[threadSort])
  }, [threadSort, threadFilter, threads])

  if (!fileDetails) {
    return null
  }

  return (
    <>
      {threadList.map(thread => (
        <Popover
          key={thread.threadId}
          isOpen={
            selectedThread?.threadId === thread.threadId ||
            hoveredThreadId === thread.threadId
          }
          trigger={
            selectedThread?.threadId !== thread.threadId &&
            hoveredThreadId === thread.threadId
              ? 'hover'
              : 'click'
          }
          placement="auto"
          isLazy
        >
          <PopoverTrigger>
            <AvatarGroup
              position="absolute"
              cursor="pointer"
              padding={
                thread.position &&
                (thread.versionId === fileDetails.versionInfo.id ||
                  (fileDetails.type === 'file' && !thread.versionId))
                  ? selectedThread?.threadId === thread.threadId ||
                    hoveredThreadId === thread.threadId
                    ? '4px'
                    : '2px'
                  : '0px'
              }
              background={
                selectedThread?.threadId === thread.threadId ||
                hoveredThreadId === thread.threadId
                  ? 'appBlue'
                  : 'black'
              }
              top={
                thread.position?.y &&
                (thread.versionId === fileDetails.versionInfo.id ||
                  (fileDetails.type === 'file' && !thread.versionId))
                  ? `${thread.position.y}%`
                  : '50%'
              }
              left={
                thread.position?.x &&
                (thread.versionId === fileDetails.versionInfo.id ||
                  (fileDetails.type === 'file' && !thread.versionId))
                  ? `${thread.position.x}%`
                  : '50%'
              }
              transform="translate(-50%, -50%)"
              boxShadow={
                thread.position &&
                (thread.versionId === fileDetails.versionInfo.id ||
                  (fileDetails.type === 'file' && !thread.versionId))
                  ? '4px 4px 4px 0 rgba(0, 0, 0, 0.2)'
                  : 'none'
              }
              size="md"
              max={2}
              onClick={() => {
                dispatch({
                  type: MediaLibraryActions.SET_SELECTED_THREAD,
                  payload: thread,
                })
              }}
              style={{
                borderRadius: '38px',
              }}
              variant="comments"
              spacing="-0.8rem"
              onMouseEnter={() => void setHoveredThreadId(thread.threadId)}
              onMouseLeave={() => void setHoveredThreadId(null)}
            >
              {thread.position &&
                (thread.versionId === fileDetails.versionInfo.id ||
                  (fileDetails.type === 'file' && !thread.versionId)) &&
                (() => {
                  const uniqueUsers = new Map<string, boolean>()

                  return thread.comments.map(comment => {
                    if (uniqueUsers.get(comment.userId)) {
                      return null
                    }

                    uniqueUsers.set(comment.userId, true)

                    return (
                      <Avatar
                        key={comment.commentId}
                        name={comment.userName}
                        border="none"
                      />
                    )
                  })
                })()}
            </AvatarGroup>
          </PopoverTrigger>
          {hoveredThreadId === thread.threadId &&
          thread.threadId !== selectedThread?.threadId ? (
            <Suspense fallback={LoadingState}>
              <CommentContainer comment={thread.comments[0]} />
            </Suspense>
          ) : (
            <Suspense
              fallback={
                null /* Spinner looks bad on file-preview's spinner below it. */
              }
            >
              <CommentThread
                fileDetails={fileDetails}
                threadList={threadList}
              />
            </Suspense>
          )}
        </Popover>
      ))}
    </>
  )
}

export default ThreadAvatarOverlay
